import * as React from 'react'

import classNames from 'classnames/bind'
import isEmpty from 'lodash/isEmpty'
import type moment from 'moment'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { matchText } from 'modules/core/lib/stringUtils'

import type { TeamViewEmployee } from 'modules/calendar/components/TeamView'
import Store from 'modules/calendar/components/TeamView/components/Store'
import TableBody from 'modules/calendar/components/TeamView/components/TableBody'
import TableHeader from 'modules/calendar/components/TeamView/components/TableHeader'

import styles from './index.module.scss'

const cx = classNames.bind(styles)

const useSearchFilter = createComputed((search: string, employees: TeamViewEmployee[]) => {
  return employees.filter((employee) => isEmpty(search) || matchText(search, employee.fullName))
})

interface Props {
  date: moment.Moment
  employees: TeamViewEmployee[]
  changingData?: boolean
  employeesWorkingDays: Record<string, string>
  scrollListener: () => void
  paginationInfo: { hasMore?: boolean; [key: string]: unknown } | null | undefined
}

const TeamViewTable = ({
  date,
  changingData = false,
  employees,
  employeesWorkingDays,
  scrollListener,
  paginationInfo,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { access } = useCurrentSession()
  const [scrollWrapper, setScrollWrapper] = React.useState<HTMLDivElement | null>(null)

  const store = React.useMemo(
    () => new Store(date, employees, access),
    [date, employees, changingData, access]
  )

  const employeesToShow = useSearchFilter(debouncedSearch, employees).get()

  return (
    <div
      className={cx('wrapper', {
        changingData,
      })}
      style={{
        height: '100%',
        width: '100%',
      }}
      ref={setScrollWrapper}
    >
      <table className={styles.table}>
        <TableHeader store={store} search={search} onSearch={onSearch} />
        <TableBody
          store={store}
          employees={employeesToShow}
          employeesWorkingDays={employeesWorkingDays}
          scrollListener={scrollListener}
          paginationInfo={paginationInfo}
          scrollWrapper={scrollWrapper}
          isSearching={Boolean(debouncedSearch)}
        />
      </table>
    </div>
  )
}

export default observer(TeamViewTable)
